import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../context/UserContext';
import { Link, useNavigate } from 'react-router-dom';
import cssClass from './RoadmapsLandingScreen.module.css';
import CustomizationModal from '../modals/CustomizationModal';
import Header from '../Header';
import Footer from '../Footer';
import Loader from '../Loader';
import begineerIcon from '../../assets/beginner-icon.svg';
import intermediateIcon from '../../assets/intermediate-icon.svg';
import advancedIcon from '../../assets/advanced-icon.svg';
import editIcon from '../../assets/edit-icon-white.svg';
import deleteIocn from '../../assets/delete-icon.svg';
import cloneIcon from '../../assets/clone-icon.svg';
import { AnimatePresence, motion } from 'framer-motion';
import { ADMIN_ROLE, changePageTitle } from '../../utils/constants';
import { useConfirm } from '../../context/ConfirmationContext';
import { v4 as uuidv4 } from 'uuid';
import { useNotification } from '../../hooks/useNotification';
import RoadmapAccessSharing from '../RoadmapAccessSharing';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
  hover: {
    // scale: 1.05,
    // borderColor: "rgba(255, 238, 0, 0.8)",
    // y: -5,
    // transition: {duration: 0.4}
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

/**
 *
 * @description This component is used to render the landing screen for the roadmaps. It contains a header, a footer, and a list of roadmaps created by the user, shared with the user and public roadmap. It also contains a button to create a new roadmap and a button to manage access to the roadmaps.
 * @returns {JSX.Element}
 */
const RoadmapsLandingScreen = () => {
  const {
    isLoading,
    userDetails,
    roadmaps,
    setRoadmaps,
    roadmapFetching,
    fetchAllRoadmapOfUser,
  } = useContext(UserContext);
  // const [roadmaps, setRoadmaps] = useState([]);
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  // const [roadmapFetching, setRoadmapFetching] = useState(false);
  const [activeButton, setActiveButton] = useState('Dashboard');
  const [showManageAccessModal, setShowManageAccessModal] = useState(false);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showLoadingNotification, resolveNotification, rejectNotification } =
    useNotification();

  useEffect(() => {
    changePageTitle('Dashboard');
    if (!isLoading && userDetails?.email) {
      fetchAllRoadmapOfUser();
    }
  }, [isLoading, userDetails]);

  const handleRoadmapCardClick = (event, roadmap) => {
    event.stopPropagation();
    // navigate(`/roadmap/${roadmap?.id}`);
    window.location.pathname = `/roadmap/${roadmap?.id}`;
  };

  const handleRoadmapEditClick = (event, roadmap) => {
    event.stopPropagation();
    navigate(`/draw-roadmap/${roadmap?.id}`);
  };

  const handleRoadmapCloneClick = (event, roadmap) => {
    console.log("This one called..................")
    event.stopPropagation();
    setSelectedRoadmap(roadmap);
  }

  const handleRoadmapDeleteClick = async (event, roadmap) => {
    event.stopPropagation();
    const confirmed = await confirm({
      type: 'delete-roadmap-confirmation',
      title: 'Are you sure you want to delete this roadmap ?',
      additionalProps: {
        cancelMessage: 'No, I don’t want to delete ',
        yesMessage: 'Yes, Delete the roadmap',
      },
    });
    if (confirmed) {
      const toastId = uuidv4();
      showLoadingNotification(toastId, 'Deleting Roadmap. Please wait');
      try {
        const payload = {
          e: userDetails?.email,
          r: roadmap?.id,
        };
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/r/remove-roadmap`,
          {
            method: 'DELETE',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );
        if (response.status === 200) {
          resolveNotification(toastId, 'Roadmap deleted successfully');
          setRoadmaps((prev) => prev.filter((r) => r.id !== roadmap.id));
        } else {
          rejectNotification(
            toastId,
            'Something went wrong. Please try again!',
          );
        }
      } catch (error) {
        console.log(error);
        rejectNotification(toastId, 'Something went wrong. Please try again!');
      }
    }
  };

  const formatText = (text) => {
    if (text.length > 1) {
      return text[0].toUpperCase() + text.slice(1);
    }
  };

  const getLevelIcon = (levelName) => {
    switch (levelName.toLowerCase()) {
      case 'begineer':
        return begineerIcon;
        break;
      case 'intermediate':
        return intermediateIcon;
        break;
      case 'advanced':
        return advancedIcon;
        break;
      default:
        return begineerIcon;
    }
  };

  const getFilteredRoadmaps = (section) => {
    if (!roadmaps || !userDetails) return [];

    switch (section) {
      case 'assigned':
        return roadmaps.filter(
          (r) => r.access === 'viewer' && r.ownerEmail !== userDetails.email,
        );
      case 'published':
        return roadmaps.filter(
          (r) => ['owner', 'editor'].includes(r.access) && r.status === 'published',
        );
      case 'draft':
        return roadmaps.filter(
          (r) =>
            ['owner', 'editor'].includes(r.access) &&
            r.status === 'draft'
        );

      default:
        return [];
    }
  };

  const onCancleManageAccess = () => {
    setShowManageAccessModal(false);
  };

  useEffect(() => {
    if (showManageAccessModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showManageAccessModal]);

  return (
    <div className={cssClass['roadmap-landing-screen']}>
      <Header />
      <div className={cssClass['header-next']}>
        <button
          className={`${
            activeButton === 'Dashboard'
              ? cssClass['header-next-btn-active']
              : cssClass['header-next-btn']
          }`}
        >
          Dashboard
        </button>
      </div>
      <div className={cssClass['main-container']}>
        <div className={cssClass['heading-div']}>
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              transition: { duration: 0.4, ease: 'easeOut' },
            }}
            className={cssClass['heading']}
          >
            Welcome, {formatText(userDetails?.email.split('@')[0])}!
          </motion.div>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              transition: { duration: 0.4, ease: 'easeOut' },
            }}
            className={cssClass['sub-heading']}
          >
            Manage and track your roadmap creations seamlessly. Below, you'll
            find all the public roadmaps, roadmap created by you and shared with
            you. Stay organized and on track with roadmaps.
          </motion.div>
        </div>
        <div className={cssClass['roadmap-header']}>
          <div className={cssClass['roadmap-header-title']}>Your Roadmaps</div>
          {userDetails?.role && userDetails?.role === ADMIN_ROLE && (
            <div className={cssClass['roadmap-header-btn']}>
              <Link
                to="/draw-roadmap/create"
                className={cssClass['roadmap-create-btn']}
              >
                New Roadmap
              </Link>
              <div
                // to="/r/access-sharing"
                className={cssClass['roadmap-access-btn']}
                onClick={() => setShowManageAccessModal(!showManageAccessModal)}
              >
                Manage Access
              </div>
            </div>
          )}
        </div>
        {roadmapFetching ? (
          <div className={cssClass['loader-div']}>
            <Loader height={80} width={80} />
          </div>
        ) : (
          <>
            {userDetails.role === ADMIN_ROLE && (
              <>
                <RoadmapSection
                  title="Draft"
                  roadmaps={getFilteredRoadmaps('draft')}
                  userDetails={userDetails}
                  onCardClick={handleRoadmapCardClick}
                  onEditClick={handleRoadmapEditClick}
                  onCloneClick={handleRoadmapCloneClick}
                  onDeleteClick={handleRoadmapDeleteClick}
                />
                <RoadmapSection
                  title="Published"
                  roadmaps={getFilteredRoadmaps('published')}
                  userDetails={userDetails}
                  onCardClick={handleRoadmapCardClick}
                  onEditClick={handleRoadmapEditClick}
                  onCloneClick={handleRoadmapCloneClick}
                  onDeleteClick={handleRoadmapDeleteClick}
                />
              </>
            )}
            <RoadmapSection
              title="Assigned"
              roadmaps={getFilteredRoadmaps('assigned')}
              userDetails={userDetails}
              onCardClick={handleRoadmapCardClick}
              onEditClick={handleRoadmapEditClick}
              onCloneClick={handleRoadmapCloneClick}
              onDeleteClick={handleRoadmapDeleteClick}
            />
          </>
        )}
      </div>
      <Footer />
      <AnimatePresence>
        {selectedRoadmap && (
          <CustomizationModal
            roadmap={selectedRoadmap}
            setSelectedRoadmap={setSelectedRoadmap}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showManageAccessModal && (
          <RoadmapAccessSharing onCancel={onCancleManageAccess} />
        )}
      </AnimatePresence>
    </div>
  );
};

const RoadmapSection = ({
  title,
  roadmaps,
  userDetails,
  onCardClick,
  onEditClick,
  onCloneClick,
  onDeleteClick,
}) => {
  return (
    <div className={cssClass['roadmap-section-ct']}>
      {userDetails.role == ADMIN_ROLE && (
        <>
          <div className={cssClass['roadmap-section-title']}>{title}</div>
          <hr className={cssClass['roadmap-section-hr']} />
        </>
      )}
      {!roadmaps || roadmaps.length === 0 ? (
        <div className={cssClass['empty-roadmap']}>
          No {title.toLowerCase()} roadmaps found.
        </div>
      ) : (
        <motion.div
          className={cssClass['roadmaps-ct']}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {roadmaps.map((roadmap) => (
            <RoadmapCard
              key={roadmap.id}
              type={title}
              roadmap={roadmap}
              userDetails={userDetails}
              onCardClick={onCardClick}
              onEditClick={onEditClick}
              onCloneClick={onCloneClick}
              onDeleteClick={onDeleteClick}
            />
          ))}
        </motion.div>
      )}
    </div>
  );
};

const RoadmapCard = ({
  type,
  roadmap,
  userDetails,
  onCardClick,
  onEditClick,
  onCloneClick,
  onDeleteClick,
}) => {
  const getLevelIcon = (levelName) => {
    switch (levelName?.toLowerCase()) {
      case 'begineer':
        return begineerIcon;
      case 'intermediate':
        return intermediateIcon;
      case 'advanced':
        return advancedIcon;
      default:
        return begineerIcon;
    }
  };

  const formatText = (text) =>
    text?.length > 1 ? text[0].toUpperCase() + text.slice(1) : text;

  return (
    <motion.div
      className={cssClass['roadmap-card']}
      variants={cardVariants}
      whileHover="hover"
      onClick={(e) => onCardClick(e, roadmap)}
    >
      <div className={cssClass['card-head']}>
        <div className={cssClass['roadmap-title']} title={roadmap?.title}>
          {roadmap?.title}
        </div>
        {['Draft', 'Published'].includes(type) && (
          <RoadmapStatusBadge type={type} />
        )}
      </div>
      <div
        className={cssClass['roadmap-desc']}
        title={roadmap?.description || 'description'}
      >
        {roadmap?.description?.trim()
          ? roadmap.description.length > 200
            ? `${roadmap.description.substring(0, 200)}...`
            : roadmap.description
          : 'No description provided'}
      </div>
      <div className={cssClass['info-div']}>
        <div className={cssClass['info']}>
          <div className={cssClass['level-info']}>
            <img src={getLevelIcon(roadmap.level)} alt="" />
            <p>{formatText(roadmap.level)}</p>
          </div>
          {/* {
            userDetails?.role===ADMIN_ROLE && (
              <div className={cssClass['creator-info']}>
                <p className={cssClass['creator-text']}>Created by : </p>
                <p className={cssClass['creator-name']} >{formatText(roadmap.created_by.split('@')[0])}</p>
              </div>
            )} */}
        </div>
          <div className={cssClass['action-div']}>
            {
              userDetails?.role === ADMIN_ROLE && (
                <button
                  className={cssClass['action-btn']}
                  onClick={(e) => onCloneClick(e, roadmap)}
                >
                  <img src={cloneIcon} alt="clone-icon" title="clone roadmap" />
                </button>
              )}
          {['editor', 'owner'].includes(roadmap?.access) && (
            <button
              className={cssClass['action-btn']}
              onClick={(e) => onEditClick(e, roadmap)}
            >
              <img src={editIcon} alt="edit-icon" title="edit roadmap" />
            </button>
            )}
            {
              roadmap?.access === 'owner' && (
              <button
                className={cssClass['action-btn']}
                onClick={(e) => onDeleteClick(e, roadmap)}
              >
                <img src={deleteIocn} alt="delete-icon" title="delete roadmap" />
              </button>
            )}
          </div>
      </div>
    </motion.div>
  );
};

const RoadmapStatusBadge = ({ type }) => {
  return (
    <>
      {type === 'Draft' ? (
        <div className={cssClass['draft-badge']}>Draft</div>
      ) : (
        <div className={cssClass['published-badge']}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="9"
            height="9"
            viewBox="0 0 9 9"
            fill="none"
          >
            <circle cx="4.5" cy="4.5" r="4.5" fill="#3867F3" />
          </svg>
          <div className={cssClass['badge-text']}>Published</div>
        </div>
      )}
    </>
  );
};

export default RoadmapsLandingScreen;
